import React, {useState, useMemo, useEffect} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import moment from 'moment'
import clsx from 'clsx'
import useNotifications from '../../../../../hooks/useNotifications'
import useAuth from '../../../../../hooks/useAuth'
import {getDefaultProfileImage} from '../../../../helpers'
import {toAbsoluteUrl} from '../../../../helpers'
import type {Notification} from '../../../../../redux/reducers/notifications/types'
import './NotificationsPanel.css'

export type NotificationsTab = 'all' | 'unread' | 'ads'

const NOTIFICATION_TYPES = {
  ADD_EXERCICE: 'ADD_EXERCICE',
  DONE_EXERCICE: 'DONE_EXERCICE',
  ADD_REMARQUE: 'ADD_REMARQUE',
  ADD_EXAM: 'ADD_EXAM',
  EDIT_EXAM_DATE: 'EDIT_EXAM_DATE',
  DELETE_EXAM: 'DELETE_EXAM',
  DONE_EXAM: 'DONE_EXAM',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  PAYMENT_CONFIRMED: 'PAYMENT_CONFIRMED',
  NEW_CHAT_MESSAGE: 'NEW_CHAT_MESSAGE',
} as const

/** Type icon: message, alert, payment */
function getNotificationTypeIcon(type: string): 'message' | 'alert' | 'payment' | null {
  if (
    type === NOTIFICATION_TYPES.ADD_REMARQUE ||
    type === NOTIFICATION_TYPES.ADD_EXERCICE ||
    type === NOTIFICATION_TYPES.NEW_CHAT_MESSAGE
  ) return 'message'
  if (type === NOTIFICATION_TYPES.PAYMENT_CONFIRMED || type === NOTIFICATION_TYPES.PASSWORD_CHANGED) return 'payment'
  if (
    type === NOTIFICATION_TYPES.DONE_EXERCICE ||
    type === NOTIFICATION_TYPES.ADD_EXAM ||
    type === NOTIFICATION_TYPES.EDIT_EXAM_DATE ||
    type === NOTIFICATION_TYPES.DELETE_EXAM ||
    type === NOTIFICATION_TYPES.DONE_EXAM
  ) return 'alert'
  return null
}

function getAvatarUrl(notification: Notification): string {
  const sender = notification.sender as any
  if (sender?.picture) return sender.picture.startsWith('http') ? sender.picture : toAbsoluteUrl(`/media/${sender.picture}`)
  if (sender?.avatar) return sender.avatar.startsWith('http') ? sender.avatar : toAbsoluteUrl(`/media/${sender.avatar}`)
  const gender = (sender?.gender || '').toLowerCase()
  return getDefaultProfileImage(gender)
}

/** Extract conversationId from chat notification URL (format: /chat/{conversationId}) */
function extractConversationIdFromUrl(url: string | undefined): string | null {
  if (!url) return null
  const match = url.match(/\/chat\/([a-f0-9]{24})/i)
  return match ? match[1] : null
}

/** Build the target URL for a notification: use backend url when set, else route by type */
function getNotificationTargetUrl(notification: Notification): string | null {
  const url = notification.url?.trim()
  if (url) return url
  const type = notification.notificationType
  if (type === NOTIFICATION_TYPES.ADD_EXERCICE || type === NOTIFICATION_TYPES.DONE_EXERCICE) return '/exercices'
  if (type === NOTIFICATION_TYPES.ADD_REMARQUE) return '/remarques'
  if (
    type === NOTIFICATION_TYPES.ADD_EXAM ||
    type === NOTIFICATION_TYPES.EDIT_EXAM_DATE ||
    type === NOTIFICATION_TYPES.DELETE_EXAM ||
    type === NOTIFICATION_TYPES.DONE_EXAM
  ) return '/exams'
  if (type === NOTIFICATION_TYPES.PAYMENT_CONFIRMED) return '/payments'
  if (type === NOTIFICATION_TYPES.PASSWORD_CHANGED) return '/profile'
  if (type === NOTIFICATION_TYPES.NEW_CHAT_MESSAGE) return url || null // Use backend URL if available
  return null
}

interface NotificationsPanelProps {
  onMoreClick?: () => void
  onSeeAllClick?: () => void
  className?: string
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  onMoreClick,
  onSeeAllClick,
  className,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {notifications, fetchNotifications, handleUnseenNotification, markAllAsRead} = useNotifications()
  const {me} = useAuth()
  const [activeTab, setActiveTab] = useState<NotificationsTab>('all')
  const [markingAll, setMarkingAll] = useState(false)
  const hasUnread = useMemo(() => notifications.some((n) => !n.read), [notifications])

  // Fetch notifications on mount and when user changes (only once)
  useEffect(() => {
    if (me?._id) {
      fetchNotifications({recipient: me._id}).catch((err) => {
        console.error('[notifications] Failed to fetch notifications:', err)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?._id]) // Only depend on me._id, not fetchNotifications (stable function)

  const filtered = useMemo(() => {
    let list = notifications.filter(
      (n) =>
        n.notificationType === NOTIFICATION_TYPES.ADD_EXERCICE ||
        n.notificationType === NOTIFICATION_TYPES.DONE_EXERCICE ||
        n.notificationType === NOTIFICATION_TYPES.ADD_REMARQUE ||
        n.notificationType === NOTIFICATION_TYPES.ADD_EXAM ||
        n.notificationType === NOTIFICATION_TYPES.EDIT_EXAM_DATE ||
        n.notificationType === NOTIFICATION_TYPES.DELETE_EXAM ||
        n.notificationType === NOTIFICATION_TYPES.DONE_EXAM ||
        n.notificationType === NOTIFICATION_TYPES.PASSWORD_CHANGED ||
        n.notificationType === NOTIFICATION_TYPES.PAYMENT_CONFIRMED ||
        n.notificationType === NOTIFICATION_TYPES.NEW_CHAT_MESSAGE
    )
    if (activeTab === 'unread') list = list.filter((n) => !n.read)
    if (activeTab === 'ads') list = list.filter((n) => n.notificationType === NOTIFICATION_TYPES.PAYMENT_CONFIRMED)
    
    return list
  }, [notifications, activeTab])

  const handleItemClick = (notification: Notification) => {
    // 1. Always mark as read on click (backend POST /seen is idempotent per NOTIFICATION-COMPLETE.md)
    handleUnseenNotification({notificationId: notification._id}).then(() => {
      if (me?._id) fetchNotifications({recipient: me._id})
    })
    
    // 2. Handle chat notifications specially (open FloatingConnectedUsers modal)
    if (notification.notificationType === NOTIFICATION_TYPES.NEW_CHAT_MESSAGE) {
      const conversationId = extractConversationIdFromUrl(notification.url)
      if (conversationId) {
        // Dispatch custom event to open chat conversation
        window.dispatchEvent(
          new CustomEvent('openChatConversation', {
            detail: { conversationId },
          })
        )
        return // Don't navigate - chat opens via FloatingConnectedUsers
      }
    }
    
    // 3. Redirect to the appropriate page (backend url or route by type)
    const targetUrl = getNotificationTargetUrl(notification)
    if (targetUrl) {
      if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
        window.open(targetUrl, '_blank')
      } else {
        navigate(targetUrl)
      }
    }
  }

  const renderMainAndSub = (notification: Notification) => {
    const type = notification.notificationType
    const exercice = notification.exercice as any
    const remarque = notification.remarque as any

    if (type === NOTIFICATION_TYPES.ADD_EXERCICE) {
      const title = intl.formatMessage({id: 'NOTIFICATION.ADD_EXERCICE.TITLE'})
      const desc = exercice?.title
        ? intl.formatMessage(
            {id: 'NOTIFICATION.ADD.EXERCICE'},
            {
              exercice: exercice.title,
              classroom: exercice?.classrooms?.map((c: any) => c.name).join(', ') || '—',
              subject: exercice?.subject?.name || '—',
            }
          )
        : ''
      return {main: title, sub: desc }
    }
    if (type === NOTIFICATION_TYPES.DONE_EXERCICE) {
      const title = intl.formatMessage({id: 'NOTIFICATION.DONE_EXERCICE.TITLE'})
      const desc = exercice?.title
        ? intl.formatMessage(
            {id: 'NOTIFICATION.DONE.EXERCICE'},
            {
              classroom: exercice?.classrooms?.map((c: any) => c.name).join(', ') || '—',
              exercice: exercice.title,
              subject: exercice?.subject?.name || '—',
            }
          )
        : ''
      return {main: title, sub: desc }
    }
    if (type === NOTIFICATION_TYPES.ADD_REMARQUE) {
      const title = intl.formatMessage({id: 'MENU.REMARQUE'})
      const from = remarque?.administration
        ? intl.formatMessage({id: 'MENU.THE_ADMIN'})
        : remarque?.teacher
          ? `${(remarque.teacher as any).firstName} ${(remarque.teacher as any).lastName}`
          : '—'
      const typeName = remarque?.type?.name || '—'
      const desc = intl.formatMessage({id: 'NOTIFICATION.ADD.REMARQUE'}, {type: typeName, from})
      return {main: title, sub: desc }
    }
    if (type === NOTIFICATION_TYPES.PAYMENT_CONFIRMED) {
      return {
        main: intl.formatMessage({id: 'NOTIFICATION.PAYMENT_CONFIRMED.TITLE', defaultMessage: 'Payment confirmed'}),
        sub: intl.formatMessage({id: 'NOTIFICATION.PAYMENT_CONFIRMED.DESC', defaultMessage: 'Your payment has been confirmed.'}),
      }
    }
    if (type === NOTIFICATION_TYPES.PASSWORD_CHANGED) {
      return {
        main: intl.formatMessage({id: 'NOTIFICATION.PASSWORD_CHANGED.TITLE', defaultMessage: 'Password changed'}),
        sub: intl.formatMessage({id: 'NOTIFICATION.PASSWORD_CHANGED.DESC', defaultMessage: 'Your password was updated successfully.'}),
      }
    }
    if (type === NOTIFICATION_TYPES.NEW_CHAT_MESSAGE) {
      const sender = notification.sender as any
      const senderName = sender
        ? `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'Someone'
        : 'Admin'
      return {
        main: intl.formatMessage(
          {id: 'NOTIFICATION.NEW_CHAT_MESSAGE.TITLE', defaultMessage: 'New message from {sender}'},
          {sender: senderName}
        ),
        sub: intl.formatMessage(
          {id: 'NOTIFICATION.NEW_CHAT_MESSAGE.DESC', defaultMessage: 'You have a new message'},
          {}
        ),
      }
    }
    return {main: type, sub: '' }
  }

  return (
    <div className={clsx('notifications-panel', className)}>
      <header className="notifications-panel__header">
        <div className="notifications-panel__title-row">
          <h2 className="notifications-panel__title">
            <FormattedMessage id="NOTIFICATIONS.TITLE" defaultMessage="Notifications" />
          </h2>
          <button
            type="button"
            className="notifications-panel__more"
            onClick={onMoreClick}
            aria-label={intl.formatMessage({id: 'MENU.MORE_OPTIONS', defaultMessage: 'More options'})}
          >
            <i className="bi bi-three-dots" />
          </button>
        </div>
        <div className="notifications-panel__tabs">
          <button
            type="button"
            className={clsx('notifications-panel__tab', activeTab === 'all' && 'notifications-panel__tab--active')}
            onClick={() => setActiveTab('all')}
          >
            <FormattedMessage id="NOTIFICATIONS.TAB_ALL" defaultMessage="All" />
          </button>
          <button
            type="button"
            className={clsx('notifications-panel__tab', activeTab === 'unread' && 'notifications-panel__tab--active')}
            onClick={() => setActiveTab('unread')}
          >
            <FormattedMessage id="NOTIFICATIONS.TAB_UNREAD" defaultMessage="Unread" />
          </button>
          <button
            type="button"
            className={clsx('notifications-panel__tab', activeTab === 'ads' && 'notifications-panel__tab--active')}
            onClick={() => setActiveTab('ads')}
          >
            <FormattedMessage id="NOTIFICATIONS.TAB_MY_ADS" defaultMessage="My Ads" />
          </button>
        </div>
        <div className="notifications-panel__see-all-row">
          {hasUnread && (
            <button
              type="button"
              className="notifications-panel__see-all notifications-panel__mark-all"
              onClick={async () => {
                if (!me?._id || markingAll) return
                setMarkingAll(true)
                try {
                  await markAllAsRead(me._id)
                } finally {
                  setMarkingAll(false)
                }
              }}
              disabled={markingAll}
            >
              {markingAll ? (
                <FormattedMessage id="NOTIFICATIONS.MARKING" defaultMessage="Marking…" />
              ) : (
                <FormattedMessage id="NOTIFICATIONS.MARK_ALL_READ" defaultMessage="Mark all as read" />
              )}
            </button>
          )}
          <button type="button" className="notifications-panel__see-all" onClick={onSeeAllClick}>
            <FormattedMessage id="NOTIFICATIONS.SEE_ALL" defaultMessage="See all" />
          </button>
        </div>
      </header>
      <div className="notifications-panel__list">
        {filtered.length === 0 ? (
          <p className="notifications-panel__empty">
            <FormattedMessage id="MENU.EMPTY_NOTIFICATIONS" />
          </p>
        ) : (
          <>
            <p className="notifications-panel__section-title">
              <FormattedMessage id="NOTIFICATIONS.SECTION_NEW" defaultMessage="New" />
            </p>
            {filtered.map((notification) => {
              const {main, sub} = renderMainAndSub(notification)
              const typeIcon = getNotificationTypeIcon(notification.notificationType)
              const avatarUrl = getAvatarUrl(notification)
              return (
                <button
                  key={notification._id}
                  type="button"
                  className={clsx(
                    'notifications-panel__item',
                    !notification.read && 'notifications-panel__item--unread'
                  )}
                  onClick={() => handleItemClick(notification)}
                >
                  <div className="notifications-panel__avatar-wrap">
                    <img
                      src={avatarUrl}
                      alt=""
                      className="notifications-panel__avatar"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement
                        t.src = getDefaultProfileImage('')
                      }}
                    />
                    {typeIcon && (
                      <span
                        className={clsx(
                          'notifications-panel__type-icon',
                          `notifications-panel__type-icon--${typeIcon}`
                        )}
                      >
                        {typeIcon === 'message' && <i className="bi bi-chat-dots-fill" />}
                        {typeIcon === 'alert' && <i className="bi bi-bell-fill" />}
                        {typeIcon === 'payment' && <i className="bi bi-credit-card-fill" />}
                      </span>
                    )}
                  </div>
                  <div className="notifications-panel__body">
                    <p className="notifications-panel__main-text">{main}</p>
                    {sub && <p className="notifications-panel__sub-text">{sub}</p>}
                    <div className="notifications-panel__meta">
                      <span className="notifications-panel__time">
                        {moment(notification.createdAt).fromNow(true)}
                      </span>
                      {!notification.read && <span className="notifications-panel__unread-dot" />}
                    </div>
                  </div>
                </button>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}
